import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ServiceType } from 'src/app/core/constants/service-type.constant';
import { environment } from 'src/environments/environment';
import { FeatureFlagsService } from '../../../../core/services/feature-flags/feature-flags.service';
import {
  IIncodeStatusResponseApi,
  ILoginIncodeResponse,
} from '../../interfaces/incode.interface';
import {
  ClientInfoResponse,
  Compatibility,
  DnValidation,
  IContractBody,
  IContractResponse,
  IDn,
  IInfoClientAvailableCredit,
  IResponseAvailableCredit,
  IResponseDnProfile,
  IResponseSaveInfoClient,
  ISaveClientInfo,
  ISaveSignature,
  IValidationDnTypeService,
  IZipCode,
  OtpResponse,
  OtpResponseValidation
} from '../../interfaces/validations.interface';
import { ValidationsServiceBase } from './validations.base';
import { FakeValidationsService } from './validations.fake.service';
import { ValidationsService } from './validations.service';

@Injectable({
  providedIn: 'root',
})
export class ValidationsProxyService extends ValidationsServiceBase {
  private readonly _realService = inject(ValidationsService);
  private readonly _fakeService = inject(FakeValidationsService);
  private readonly _flags = inject(FeatureFlagsService);
  private readonly _fakesEnabled = !environment.production;

  override checkCompatibility(imei: string): Observable<Compatibility> {
    return this._fakesEnabled &&
      this._flags.fakeFlags?.compatibilityView.compatibility
      ? this._fakeService.checkCompatibility(imei)
      : this._realService.checkCompatibility(imei);
  }

  override ValidateDn(dn: DnValidation): Observable<OtpResponse> {
    return this._fakesEnabled &&
      this._flags.fakeFlags?.numberVerificationView.otp
      ? this._fakeService.ValidateDn(dn)
      : this._realService.ValidateDn(dn);
  }

  override validateOtp(
    code: string,
    idDn: string,
    imei: string,
  ): Observable<OtpResponseValidation> {
    return this._fakesEnabled &&
      this._flags.fakeFlags?.numberVerificationView.otp
      ? this._fakeService.validateOtp(code, idDn, imei)
      : this._realService.validateOtp(code, idDn, imei);
  }

  override validateOtpPortability(
    code: string,
    idDn: string,
  ): Observable<OtpResponseValidation> {
    return this._fakesEnabled &&
      this._flags.fakeFlags?.numberVerificationView.otp
      ? this._fakeService.validateOtpPortability(code, idDn)
      : this._realService.validateOtpPortability(code, idDn);
  }

  override validateDnType(dn: string): Observable<IValidationDnTypeService> {
    return this._fakesEnabled &&
      this._flags.fakeFlags?.numberVerificationView.otp
      ? this._fakeService.validateDnType(dn)
      : this._realService.validateDnType(dn);
  }

  override profileDn(value: IDn, type: string): Observable<IResponseDnProfile> {
    return this._fakesEnabled &&
      this._flags.fakeFlags?.formDataView.simValidation
      ? this._fakeService.profileDn(value, type)
      : this._realService.profileDn(value, type);
  }

  override loginIncode(): Observable<ILoginIncodeResponse> {
    return this._fakesEnabled &&
      this._flags.fakeFlags?.identityVerificationView.incode
      ? this._fakeService.loginIncode()
      : this._realService.loginIncode();
  }

  override getValidateStepIncode(
    sessionId: string,
    token: string,
  ): Observable<IIncodeStatusResponseApi> {
    return this._fakesEnabled &&
      this._flags.fakeFlags?.identityVerificationView.incode
      ? this._fakeService.getValidateStepIncode(sessionId, token)
      : this._realService.getValidateStepIncode(sessionId, token);
  }

  override getClientInfo(sessionId: string): Observable<ClientInfoResponse> {
    return this._fakesEnabled &&
      this._flags.fakeFlags?.identityVerificationView.incode
      ? this._fakeService.getClientInfo(sessionId)
      : this._realService.getClientInfo(sessionId);
  }

  override getColony(colony: string): Observable<IZipCode> {
    return this._fakesEnabled && this._flags.fakeFlags?.formDataView.zipCode
      ? this._fakeService.getColony(colony)
      : this._realService.getColony(colony);
  }

  override saveClientInfo(
    dataClient: ISaveClientInfo,
  ): Observable<IResponseSaveInfoClient> {
    return this._fakesEnabled && this._flags.fakeFlags?.formDataView.saveData
      ? this._fakeService.saveClientInfo(dataClient)
      : this._realService.saveClientInfo(dataClient);
  }

  override saveClientPortabilityInfo(
    dataClient: ISaveClientInfo,
  ): Observable<IResponseSaveInfoClient> {
    return this._fakesEnabled && this._flags.fakeFlags?.formDataView.saveData
      ? this._fakeService.saveClientPortabilityInfo(dataClient)
      : this._realService.saveClientPortabilityInfo(dataClient);
  }

  override getOffersAvailableCredit(
    code: string,
    idDn: string,
    clientInfo: IInfoClientAvailableCredit,
    classification: string,
  ): Observable<IResponseAvailableCredit> {
    return this._fakesEnabled && this._flags.fakeFlags?.creditHistoryView.credit
      ? this._fakeService.getOffersAvailableCredit(
          code,
          idDn,
          clientInfo,
          classification,
        )
      : this._realService.getOffersAvailableCredit(
          code,
          idDn,
          clientInfo,
          classification,
        );
  }

  override getInfoContract(
    offer: IContractBody,
    uuidPreregister: string,
  ): Observable<IContractResponse> {
    return this._fakesEnabled && this._flags.fakeFlags?.contractView.contract
      ? this._fakeService.getInfoContract(offer, uuidPreregister)
      : this._realService.getInfoContract(offer, uuidPreregister);
  }

  override saveLegalsSignature(
    signature: ISaveSignature,
    uuid: string,
  ): Observable<unknown> {
    return this._fakesEnabled && this._flags.fakeFlags?.contractView.sign
      ? this._fakeService.saveLegalsSignature(signature, uuid)
      : this._realService.saveLegalsSignature(signature, uuid);
  }

  override viewPortability(uuidContract: string): Observable<unknown> {
    return this._fakesEnabled && this._flags.fakeFlags?.contractView.portability
      ? this._fakeService.viewPortability(uuidContract)
      : this._realService.viewPortability(uuidContract);
  }

  override noPaymentContract(
    uuidContract: string,
    typeService: ServiceType,
  ): Observable<unknown> {
    return this._fakesEnabled && this._flags.fakeFlags?.contractView.noPayment
      ? this._fakeService.noPaymentContract(uuidContract, typeService)
      : this._realService.noPaymentContract(uuidContract, typeService);
  }
}
